import { effect, inject, Injectable, signal } from '@angular/core';
import Keycloak from 'keycloak-js';
import type { ReadyArgs } from 'keycloak-angular';
import { KEYCLOAK_EVENT_SIGNAL, KeycloakEventType, typeEventArgs } from 'keycloak-angular';
import type { User } from './model/user';
import type { UserRole } from './model/user-roles';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly keycloak = inject(Keycloak);

  // eslint-disable-next-line @typescript-eslint/naming-convention
  private readonly _isSignedIn = signal(false);
  // eslint-disable-next-line @typescript-eslint/naming-convention
  private readonly _user = signal<User | null>(null);

  readonly isSignedIn = this._isSignedIn.asReadonly();
  readonly user = this._user.asReadonly();

  constructor() {
    const keycloakSignal = inject(KEYCLOAK_EVENT_SIGNAL);

    effect(() => {
      const keycloakEvent = keycloakSignal();
      if (keycloakEvent.type === KeycloakEventType.Ready) {
        this._isSignedIn.set(typeEventArgs<ReadyArgs>(keycloakEvent.args));
      }

      if (keycloakEvent.type === KeycloakEventType.AuthLogout) {
        this._isSignedIn.set(false);
      }

      if (keycloakEvent.type === KeycloakEventType.AuthSuccess) {
        this._isSignedIn.set(true);
      }
    });

    effect(() => {
      if (this._isSignedIn()) {
        this.keycloak.loadUserInfo().then(profile => {
          this._user.set({
            name: profile['preferred_username'] ?? 'unknown',
          });
        });
      } else {
        this._user.set(null);
      }
    });
  }

  login(): void {
    this.keycloak.login();
  }

  logout(): void {
    this.keycloak.logout();
  }

  hasRole(role: UserRole): boolean {
    return this.keycloak.hasRealmRole(role);
  }

  goToAccount(): void {
    this.keycloak.accountManagement();
  }
}
